import { useState, useEffect, useCallback } from "react";
import AppLayout from "../../components/Layout";
import {
  Col,
  Row,
  Select,
  notification,
  GetProp,
  Upload,
  UploadFile,
  UploadProps,
  Button,
  Spin,
} from "antd";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getClientList,
  getSmsTemplates,
  getBuldSMSTemplatePreview,
  getWhatsappTeplates,
  getEmailTemplates,
  getTemplatePreview,
} from "../../services/commonController";
import { isEmpty, getUserData } from "../../utils";
import { AdminTable } from "./component/AdminTable";
import { AgentTable } from "./component/AgentTable";
import { ManagerTable } from "./component/ManagerTable";
import { PreviewModal } from "./component/PreviewModal";
import MediationModal from "./component/MediationModal";
import { AdvanceFilter } from "../../components/AdvanceFilter";
import { FilterOutlined } from "@ant-design/icons";
import { useGlobalStore } from "../../contexts/StoreContext";
import { getCasePayload, getCasePayloadType, retCasesWord } from "../../utils";
import { SearchBox } from "../../components/SearchBox";

const tempMessageTypeList = ["SMS", "Email"];
type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

interface smsTemplateProps {
  id: string;
  tempName: string;
}

interface emailTemplateProps {
  id: string;
  name: string;
  templateType?: string;
  templateName?: string;
}
export interface MediationFormData {
  mediationDate: string;
  mediationTime: string;
  mediationMode: string;
  mediationLink: string;
  meetingId: string;
  passcode: string;
}

export const BulkMessage: React.FC = () => {
  const [states, setStates] = useState<{
    loader: boolean;
    selectedMsgType: string;
    selectedNBFC: string;
    smsTemplatesList: smsTemplateProps[];
    selectedSmsTemplate: smsTemplateProps;
    emailTemplatesList: emailTemplateProps[];
    selectedEmailTemplate: emailTemplateProps;
    selectedWhatsappTemplateOption: any;
    selectedWhatsappType: string;
    fileList: UploadFile[];
    templateMsg: string;
    emailPreviewLoading: boolean;
    previewModal: boolean;
    previewIsHtml: boolean;
    mediationModal: boolean;
    disabledSubmit: boolean;
    selectedRowKeys: string[];
    totalCaseData: any[];
    mediationData: MediationFormData | null;
  }>({
    loader: false,
    selectedMsgType: "",
    selectedNBFC: "",
    smsTemplatesList: [] as smsTemplateProps[],
    selectedSmsTemplate: {} as smsTemplateProps,
    emailTemplatesList: [] as emailTemplateProps[],
    selectedEmailTemplate: {} as emailTemplateProps,
    selectedWhatsappTemplateOption: {},
    selectedWhatsappType: "",
    fileList: [],
    templateMsg: "",
    emailPreviewLoading: false,
    previewModal: false,
    previewIsHtml: false,
    mediationModal: false,
    disabledSubmit: true,
    selectedRowKeys: [] as string[],
    totalCaseData: [],
    mediationData: null,
  });
  const queryClient = useQueryClient();
  const userData = getUserData();
  const [isAdvanceFilterOpen, setIsAdvanceFilterOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [searchVal, setSearchVal] = useState<string[]>([]);
  const { state, dispatch } = useGlobalStore();
  const payload = getCasePayload(state);

  useEffect(() => {
    let updatePayload = {
      dpdStatus: "",
      endDate: "",
      maxDueAmount: "",
      maxLoanAmount: "",
      minDueAmount: "",
      minLoanAmount: "",
      nbfc: "",
      page: 1,
      size: 10,
      startDate: "",
      status: "",
      sortBy: "",
      sortOrder: "",
    };
    dispatch({
      type: "SET_ACTIVE_CASE_PAYLOAD",
      payload: {
        ...payload,
        ...updatePayload,
        loanIds: [],
      },
    });
    dispatch({
      type: "SET_PENDING_CASE_PAYLOAD",
      payload: {
        ...payload,
        ...updatePayload,
        loanIds: [],
      },
    });
  }, [dispatch]);

  const handleMediationSubmit = (formData: MediationFormData) => {
    setStates((prev) => ({
      ...prev,
      mediationData: formData,
      mediationModal: false,
    }));
  };

  const casePayloadType: any = getCasePayloadType(state.activeTabKey);
  const onSelectChange = (newSelectedRowKeys: any) => {
    setStates((prev) => ({ ...prev, selectedRowKeys: newSelectedRowKeys }));
  };
  const rowSelection = {
    type: "checkbox",
    selectedRowKeys: states.selectedRowKeys,
    onChange: onSelectChange,
  };
  const tableProps = { rowSelection: rowSelection };

  const { data } = useQuery({
    queryKey: ["clientList"],
    queryFn: getClientList,
    refetchOnWindowFocus: "always",
  });
  const { data: whatsappTemplatesResponse } = useQuery({
    queryKey: ["whatsapp-templates"],
    queryFn: getWhatsappTeplates,
    refetchOnWindowFocus: "always",
  });
  const { data: emailTemplatesResponse, isLoading: emailTemplatesLoading } = useQuery({
    queryKey: ["email-templates"],
    queryFn: getEmailTemplates,
    refetchOnWindowFocus: "always",
  });
  const toggleAdvanceFilter = () => {
    setIsAdvanceFilterOpen(!isAdvanceFilterOpen);
  };

  async function onSmsTemplateSelect(value: any) {
    setStates((prev) => ({ ...prev, loader: true }));
    try {
      const previewApiRes = await getBuldSMSTemplatePreview({
        tempId: value,
      });
      setStates((prev) => ({
        ...prev,
        templateMsg:
          isEmpty(previewApiRes?.data?.data) == true
            ? ""
            : previewApiRes?.data?.data,
        loader: false,
      }));
    } catch (error: any) {
      setStates((prev) => ({
        ...prev,
        selectedSmsTemplate: {} as smsTemplateProps,
        loader: false,
      }));
      notification.error({
        message: "Error",
        description: error?.response?.data?.message
          ? error?.response?.data?.message
          : "Something went wrong! while fetching email preview.",
      });
    }
  }

  async function onEmailTemplateSelect(templateId: string) {
    if (states.selectedRowKeys.length === 0) {
      notification.warning({
        message: "No Cases Selected",
        description: "Please select at least one case to preview the email.",
      });
      return;
    }

    setStates((prev) => ({ ...prev, emailPreviewLoading: true }));
    try {
      const caseId = states.selectedRowKeys[0];
      const previewApiRes = await getTemplatePreview({
        caseId: caseId,
        tempId: templateId,
      });
      setStates((prev) => ({
        ...prev,
        templateMsg:
          isEmpty(previewApiRes?.data?.data) == true
            ? ""
            : previewApiRes?.data?.data,
        emailPreviewLoading: false,
      }));
    } catch (error: any) {
      setStates((prev) => ({
        ...prev,
        selectedEmailTemplate: {} as emailTemplateProps,
        emailPreviewLoading: false,
      }));
      notification.error({
        message: "Error",
        description: error?.response?.data?.message
          ? error?.response?.data?.message
          : "Something went wrong while fetching email preview.",
      });
    }
  }

  const fetchSmsTemplate = async (clientName: string) => {
    setStates((prev) => ({ ...prev, loader: true }));
    const res = await getSmsTemplates({ clientName });

    if (res.status !== 200) {
      setStates((prev) => ({ ...prev, loader: false }));
      notification.error({
        message: "Error",
        description: res?.data?.message,
      });
    }
    if (res.status === 200) {
      const tempArr = res?.data?.map((item: any) => {
        return {
          id: item.id,
          tempName: item.tempName,
        };
      });
      setStates((prev) => ({
        ...prev,
        smsTemplatesList: tempArr,
        selectedSmsTemplate: {} as smsTemplateProps,
        loader: false,
      }));
    }
  };

  useEffect(() => {
    if (isEmpty(states.selectedWhatsappTemplateOption) == false) {
      const findElem = whatsappTemplatesResponse?.data.find(
        (element: any) =>
          element.id == states.selectedWhatsappTemplateOption?.key
      );
      setStates((prev) => ({
        ...prev,
        templateMsg:
          isEmpty(findElem) || isEmpty(findElem?.data) ? "" : findElem.data,
      }));
    }
  }, [states.selectedWhatsappTemplateOption]);

  useEffect(() => {
    if (emailTemplatesResponse?.data && Array.isArray(emailTemplatesResponse.data)) {
      const emailTemplates = emailTemplatesResponse.data.map((template: any) => ({
        id: template.id,
        name: template.name || template.templateName || "",
        templateName: template.templateName || template.name || "",
        templateType: template.templateType || template.type || "",
      }));
      setStates((prev) => ({
        ...prev,
        emailTemplatesList: emailTemplates,
      }));
    }
  }, [emailTemplatesResponse]);

const selectedEmailTemplateIsMediation =
  states.selectedEmailTemplate &&
  (
    states.selectedEmailTemplate.templateName ===
      "Online Mediation Camp Invitation" ||
    states.selectedEmailTemplate.name ===
      "Online Mediation Camp Invitation" ||
    states.selectedEmailTemplate.templateName === "Mediation Notice" ||
    states.selectedEmailTemplate.name === "Mediation Notice" ||
    states.selectedEmailTemplate.templateType?.toLowerCase() === "mediation" ||
    states.selectedEmailTemplate.name?.toLowerCase() === "mediation"
  );
  

  const uploadProps: UploadProps = {
    multiple: false,
    action: "#",
    accept: "xlsx, XLSX, xls, XLS, doc, docx, pdf",
    customRequest: () => {
      // console.log("custom request");
    },
    onRemove: (file: any) => {
      setStates((prev) => ({ ...prev, fileList: [] }));
    },
    beforeUpload: (file: any) => {
      setStates((prev) => ({ ...prev, fileList: [file] }));
      return true;
    },
    fileList: states.fileList,
    maxCount: 1,
  };

  const handleUpload = async () => {
    setStates((prev) => ({ ...prev, disabledSubmit: true, loader: true }));
    const {
      selectedRowKeys,
      selectedMsgType,
      selectedWhatsappTemplateOption,
      selectedSmsTemplate,
      selectedEmailTemplate,
    } = states;

    const caseIds = states.selectedRowKeys.join(",");
    let smsPayload = {};
    if (states.selectedMsgType === "SMS") {
      smsPayload = {
        caseId: states.selectedRowKeys,
        tempId: Number(states.selectedSmsTemplate.id),
      };
    }

    if (
      selectedRowKeys.length === 0 ||
      (selectedMsgType === "SMS" && !selectedSmsTemplate?.id) ||
      (selectedMsgType === "Email" && !selectedEmailTemplate?.id) ||
      (selectedMsgType === "WhatsApp" &&
        (!selectedWhatsappTemplateOption?.key ||
          !selectedWhatsappTemplateOption?.children)) ||
      (selectedMsgType === "Email" &&
        selectedEmailTemplateIsMediation &&
        (!states.mediationData ||
          !states.mediationData.mediationDate ||
          !states.mediationData.mediationTime ||
          !states.mediationData.mediationLink ||
          !states.mediationData.mediationMode))
    ) {
      notification.error({
        message: "Missing Required Fields",
        description:
          selectedMsgType === "Email" && selectedEmailTemplateIsMediation
            ? "Please provide mediation details for the selected mediation template."
            : "Please select cases and a valid template before uploading.",
      });
      if (selectedMsgType === "Email" && selectedEmailTemplateIsMediation) {
        setStates((prev) => ({ ...prev, mediationModal: true }));
      }
      setStates((prev) => ({ ...prev, loader: false, disabledSubmit: false }));
      return;
    }

    const basePayload = {
      caseId: states.selectedRowKeys,
      tempId: states.selectedEmailTemplate?.id,
      templateName:
        states.selectedEmailTemplate.templateName ||
        states.selectedEmailTemplate.name ||
        "",
      ...(selectedEmailTemplateIsMediation && {
        templateType:
          states.selectedEmailTemplate.templateType?.toLowerCase() === "mediation"
            ? states.selectedEmailTemplate.templateType
            : "mediation",
      }),
    } as any;

    if (selectedEmailTemplateIsMediation) {
      basePayload.mediationRequest = {
        mediationDate: states.mediationData?.mediationDate || "",
        mediationLink: states.mediationData?.mediationLink || "",
        mediationMode: states.mediationData?.mediationMode || "",
        mediationTime: states.mediationData?.mediationTime || "",
        meetingId: states.mediationData?.meetingId || "",
        passcode: states.mediationData?.passcode || "",
      };
    }

    const actionUrl =
      states.selectedMsgType === "SMS"
        ? `${process.env.REACT_APP_API_BASE_URL}send-bulk-sms`
        : states.selectedMsgType === "Email"
        ? `${process.env.REACT_APP_API_BASE_URL}send-bulk-email`
        : `${process.env.REACT_APP_API_BASE_URL}send-bulk-whatsapp?caseId=${caseIds}&id=${states.selectedWhatsappTemplateOption?.key}&vertical=${states.selectedWhatsappTemplateOption?.children}`;

    await fetch(actionUrl, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body:
        states.selectedMsgType === "SMS"
          ? JSON.stringify(smsPayload)
          : states.selectedMsgType === "Email"
          ? JSON.stringify(basePayload)
          : JSON.stringify({
              ...states.mediationData,
              caseIds: states.selectedRowKeys,
              templateId: states.selectedWhatsappTemplateOption?.key,
              vertical: states.selectedWhatsappTemplateOption?.children,
            }),
    })
      .then((res) => res.json())
      .then((res) => {
        
  if (res?.status === 429) {
    notification.error({
      message: "Error",
      description:res?.data?.message || "Too many requests. Please try again later.",
    });

    return; 
  }
   
        notification.success({
           message:`${states.selectedMsgType === "SMS"
      ? "SMS sent successfully"
      : states.selectedMsgType === "Email"
      ? "Email sent successfully"
      : "WhatsApp message sent successfully"}`,
           
          description: "",
        });
        queryClient.invalidateQueries({ queryKey: ["case-details"] });
      })
      .catch((err) => {
        setStates((prev) => ({
          ...prev,
          disabledSubmit: false,
          loader: false,
        }));
        console.log(err,"---->err")
        notification.error({
          message: "Error",
          description: err?.response?.data?.message,
        });
      })
      .finally(() => {
        setStates((prev) => ({
          ...prev,
          selectedMsgType: "",
          selectedNBFC: "",
          selectedWhatsappType: "",
          selectedWhatsappTemplateOption: {},
          selectedSmsTemplate: {} as smsTemplateProps,
          selectedEmailTemplate: {} as emailTemplateProps,
          selectedRowKeys: [],
          disabledSubmit: true,
          fileList: [],
          loader: false,
          templateMsg: "",
          previewIsHtml: false,
        }));
      });
  };


  const handleOnLoader = useCallback(() => {
    setLoader(true);
  }, []);

  const handleOnSearch = useCallback(
    (ids: string[]) => {
      setSearchVal(ids);
      localStorage.setItem("searchInputVal", JSON.stringify(ids));

      dispatch({
        type: casePayloadType,
        payload: {
          ...payload,
          page: 1,
          loanIds: ids,
        },
      });
    },
    [dispatch, casePayloadType, payload]
  );

  const handleOnChange = useCallback(
    (page: number, pageSize: number) => {
      dispatch({
        type: casePayloadType,
        payload: {
          ...payload,
          page,
          size: pageSize,
        },
      });
    },
    [dispatch, casePayloadType, payload]
  );

  const handleOnClear = useCallback(() => {
    setSearchVal([]);
    localStorage.removeItem("searchInputVal"); // Clear localStorage
    handleOnChange(1, 10);
  }, [handleOnChange]);


console.log(states.emailTemplatesList,"states.emailTemplatesList")

  return (
    <AppLayout moduleName="Bulk Message">
      <Spin spinning={states.loader} tip="Sending ...">
        <Col>
        <Row className="mt-2 mb-2 gap-4">
          <Col span={4}>
            <Select
              onClear={() =>
                setStates((prev) => ({ ...prev, selectedMsgType: "" }))
              }
              style={{ width: "100%" }}
              placeholder="Select Message Type"
              allowClear
              onChange={(e) =>
                setStates((prev) => ({
                  ...prev,
                  selectedMsgType: e ?? "",
                  selectedSmsTemplate: {} as smsTemplateProps,
                  selectedEmailTemplate: {} as emailTemplateProps,
                  selectedWhatsappTemplateOption: {},
                  templateMsg: "",
                }))
              }
              value={states.selectedMsgType || undefined}
              options={tempMessageTypeList?.map((item: string) => ({
                key: item,
                value: item,
              }))}
            />
          </Col>
          {(states.selectedMsgType as string) === "SMS" && (
            <Col span={4}>
              <Select
                onClear={() =>
                  setStates((prev) => ({ ...prev, selectedNBFC: "" }))
                }
                style={{ width: "100%" }}
                placeholder="Select Lender"
                allowClear
                onChange={(e) =>
                  setStates((prev) => ({
                    ...prev,
                    selectedNBFC: e ?? "",
                    selectedSmsTemplate: {} as smsTemplateProps,
                  }))
                }
                value={states.selectedNBFC || undefined}
                options={data?.clients?.map((item: any) => ({
                  key: item.clientId,
                  value: item.clientId,
                  label: item.clientName,
                }))}
                onSelect={(e, options) => fetchSmsTemplate(`${options?.label}`)}
              />
            </Col>
          )}
          {(states.selectedMsgType as string) === "SMS" &&
            states.selectedNBFC.length > 0 && (
              <Col span={6}>
                <Select
                  onClear={() =>
                    setStates((prev) => ({
                      ...prev,
                      selectedSmsTemplate: {} as smsTemplateProps,
                    }))
                  }
                  allowClear
                  onChange={(value, option: any) => {
                    const tempVal = {
                      id: `${option?.key}`,
                      tempName: option?.children?.[1],
                    } as smsTemplateProps;
                    setStates((prev: any) => ({
                      ...prev,
                      selectedSmsTemplate: tempVal,
                    }));
                  }}
                  value={states.selectedSmsTemplate.tempName || undefined}
                  placeholder="Select Template"
                  style={{ width: "100%" }}
                  disabled={states.loader}
                  onSelect={(e, options) =>
                    onSmsTemplateSelect(`${options?.value}`)
                  }
                >
                  {states.smsTemplatesList?.map(
                    ({ id, tempName }: smsTemplateProps) => {

                      return (
                        <Select.Option key={id} value={id}>
                          {" "}
                          {tempName}{" "}
                        </Select.Option>
                      );
                    }
                  )}
                </Select>
              </Col>
            )}
          {(states.selectedMsgType as string) === "WhatsApp" && (
            <Col span={6}>
              <Select
                onChange={(value, option: any) =>
                  setStates((prev) => ({
                    ...prev,
                    selectedWhatsappTemplateOption: option,
                    selectedWhatsappType: whatsappTemplatesResponse?.data?.find(
                      (ele: any) => ele?.vertical === option?.children
                    )?.type,
                  }))
                }
                value={states.selectedWhatsappTemplateOption?.key || undefined}
                placeholder="Select Template"
                style={{ width: "100%" }}
              >
                {whatsappTemplatesResponse?.data?.map(
                  ({ vertical, id }: any) => {
                    return (
                      <Select.Option key={id} value={id}>
                        {vertical}
                      </Select.Option>
                    );
                  }
                )}
              </Select>
            </Col>
          )}
          {(states.selectedMsgType as string) === "Email" && (
            <Col span={6}>
              <Select
                loading={emailTemplatesLoading}
                onClear={() =>
                  setStates((prev) => ({
                    ...prev,
                    selectedEmailTemplate: {} as emailTemplateProps,
                    templateMsg: "",
                    mediationData: null,
                  }))
                }
                allowClear
               onChange={(value, option: any) => {
  const selectedTemplate = states.emailTemplatesList.find(
    (t) => t.id === value
  );

  const isMediation =
    selectedTemplate?.templateName === "Online mediation Camp Invitation" ||
    selectedTemplate?.name === "Online mediation Camp Invitation";

  setStates((prev) => ({
    ...prev,
    selectedEmailTemplate:
      selectedTemplate || ({} as emailTemplateProps),
    mediationData: null,
    mediationModal: isMediation,
  }));
}}
                value={states.selectedEmailTemplate.id || undefined}
                placeholder="Select Email Template"
                style={{ width: "100%" }}
                disabled={states.emailPreviewLoading || emailTemplatesLoading}
                onSelect={(e, options) =>
                  onEmailTemplateSelect(`${options?.value}`)
                }
              >
                {states.emailTemplatesList?.map(
                  ({ id, name }: emailTemplateProps) => {
                    return (
                      <Select.Option key={id} value={id}>
                        {name}
                      </Select.Option>
                    );
                  }
                )}
              </Select>
            </Col>
          )}

          {/* {(states.selectedMsgType as string) === "WhatsApp" &&
            (states.selectedWhatsappType as string) === "DOCUMENT" && (
              <Col>
                <Upload {...uploadProps}>
                  <Button shape="round" icon={<CloudUploadOutlined />} />
                </Upload>
              </Col>
            )} */}
          <Button
            type="default"
            disabled={
              states.selectedWhatsappType.length > 0 ||
              states.selectedSmsTemplate?.tempName?.length > 0 ||
              states.selectedEmailTemplate?.name?.length > 0
                ? false
                : true
            }
            htmlType="button"
            onClick={() => {
              const isEmailPreview = states.selectedMsgType === "Email";
              setStates((prev) => ({
                ...prev,
                previewModal: true,
                previewIsHtml: isEmailPreview,
              }));
            }}
          >
            Preview
          </Button>
          {(states.selectedWhatsappTemplateOption.children ===
            "SE_Mediation_Template" ||
            selectedEmailTemplateIsMediation) && (
            <Button
              type="default"
              htmlType="button"
              onClick={() =>
                setStates((prev) => ({ ...prev, mediationModal: true }))
              }
            >
              Meeting Details
            </Button>
          )}

          <Button 
            type="primary" 
            htmlType="submit" 
            onClick={handleUpload}
            disabled={states.loader}
            loading={states.loader}
          >
            Send Text
          </Button>
        </Row>
        <Row className="flex justify-between">
          <Col className="flex pb-1">
            <SearchBox
              placeholder="Search..."
              onLoader={handleOnLoader}
              onSearch={(val: string[]) => {
                handleOnSearch(val);
                setLoader(false);
              }}
              onClear={handleOnClear}
              loader={loader}
              initialValue={searchVal}
            />
          </Col>
          <Col className="p-1 pr-2 gap-3 flex pb-4">
            <FilterOutlined
              className="cursor-pointer text-lg"
              onClick={toggleAdvanceFilter}
            />
          </Col>
        </Row>
        {isAdvanceFilterOpen && (
          <Row className="pb-4">
            <AdvanceFilter toggleAdvanceFilter={toggleAdvanceFilter} />
          </Row>
        )}
        {userData?.data?.roleName === "Admin" && (
          <AdminTable tableProps={tableProps} />
        )}
        {userData?.data?.roleName === "Agent" && (
          <AgentTable tableProps={tableProps} />
        )}
        {userData?.data?.roleName === "Agent-Manager" && (
          <ManagerTable tableProps={tableProps} />
        )}
      </Col>
      </Spin>
      {states.previewModal && (
        <PreviewModal
          status={states.previewModal}
          onCancel={() =>
            setStates((prev) => ({ ...prev, previewModal: false }))
          }
          messageData={states.templateMsg}
          isHtml={states.previewIsHtml}
        />
      )}
      {states.mediationModal && (
        <MediationModal
          open={states.mediationModal}
          onCancel={() =>
            setStates((prev) => ({ ...prev, mediationModal: false }))
          }
          onSubmit={handleMediationSubmit}
        />
      )}
    </AppLayout>
  );
};
